// Currency formatting and conversion utilities
//
// The pure formatters live in @releve/shared/format/currency, where locale is
// an explicit argument (the API renders documents with no browser i18n). This
// module is the web-side binding: it injects the viewer's language and keeps
// the supabase-backed conversion helpers.

import { supabase } from "@/integrations/supabase/client";
// The bare i18next singleton — the same instance `@/i18n` configures at app
// startup. Importing it (rather than `@/i18n`) reads the current language
// without pulling the init side effects (react-i18next binding, HTTP backend)
// into every module that formats a number.
import i18n from "i18next";
// MultiCurrencyTotal + mergeMultiCurrencyTotals are the single source of truth in
// @releve/shared (also consumed by the bookkeeper AI's balance-sheet assembler).
// Re-exported here so the many web callers importing them from '@/lib/currency'
// keep working.
import {
  type MultiCurrencyTotal,
  mergeMultiCurrencyTotals,
} from "@releve/shared";
import { localeForLanguage } from "@releve/shared/format/locale";
import {
  formatAccountCurrency as formatAccountCurrencyForLanguage,
  formatCurrency as formatCurrencyInLocale,
  formatEntryAccountCurrency as formatEntryAccountCurrencyForLanguage,
  formatMultiCurrencyTotal as formatMultiCurrencyTotalForLanguage,
  formatOrganizationCurrency as formatOrganizationCurrencyForLanguage,
  getCurrencyName as getCurrencyNameInLocale,
  getCurrencySymbol as getCurrencySymbolInLocale,
  type EntryWithAccount,
  type MultiCurrencyTotalOptions,
} from "@releve/shared/format/currency";

export {
  SUPPORTED_CURRENCIES,
  ORG_BASE_CURRENCIES,
  normalizeCurrencyCode,
  resolveCurrency,
  resolveImportCurrency,
  resolveAccountCurrency,
  importMatchesAccountCurrency,
  findEntryBySide,
  getEntryAccountCurrency,
  journalCurrencyFromEntries,
  isSupportedCurrency,
  sumByCurrency,
  addToMultiCurrencyTotal,
  isMultiCurrencyTotalEmpty,
  nonZeroCurrencies,
  soleCurrency,
  multiCurrencyTotalsEqual,
  parseLocalizedNumber,
} from "@releve/shared/format/currency";
export type { SupportedCurrency, CurrencyAmount } from "@releve/shared/format/currency";

/**
 * A map of currency codes to their total amounts, e.g. `{ EUR: 12500, USD: 5000 }`.
 * Defined in @releve/shared; re-exported here for existing '@/lib/currency' importers.
 */
export type { MultiCurrencyTotal };

// mergeMultiCurrencyTotals is defined in @releve/shared and re-exported here.
export { mergeMultiCurrencyTotals };

/**
 * True when `currency` is a currency other than the org's (a US account in a
 * EUR org). Blank on either side is no evidence — false. A line paid in a
 * foreign currency never carries VAT: the VAT accounts are org-currency, so
 * it would also break the single-currency gate.
 */
export function isForeignCurrency(
  currency: string | null | undefined,
  orgCurrency: string | null | undefined,
): boolean {
  const code = currency?.trim().toUpperCase();
  const orgCode = orgCurrency?.trim().toUpperCase();
  return !!code && !!orgCode && code !== orgCode;
}

/**
 * Get the number-formatting locale for the viewer's UI language.
 *
 * The locale follows the VIEWER (i18next language), not the currency, so a
 * page mixing EUR and USD amounts renders one consistent number convention
 * (e.g. "€7,886.24" and "$140.40" in an English UI). Regional language codes
 * ("nl-BE") map by primary subtag; unknown languages and the pre-init window
 * (`i18n.language` undefined) fall back to 'en-US'.
 *
 * @param _currency - Ignored. Retained so existing `getCurrencyLocale(currency)`
 *   call sites compile unchanged; the currency no longer influences the locale.
 * @returns Locale string for proper number formatting
 */
export function getCurrencyLocale(_currency?: string): string {
  return localeForLanguage(i18n.language);
}

/**
 * Format an amount using the specified currency
 * @param amount - The numeric amount to format
 * @param currency - ISO 4217 currency code (e.g., 'EUR', 'USD', 'GBP')
 * @param locale - Optional locale for formatting (defaults to the viewer's
 *   UI-language locale via `getCurrencyLocale()`)
 * @returns Formatted currency string
 */
export function formatCurrency(amount: number, currency: string = 'EUR', locale: string = getCurrencyLocale()): string {
  return formatCurrencyInLocale(amount, currency, locale);
}

/**
 * Currency symbol ('€', '$', 'US$' …) in the VIEWER's locale, so a glyph
 * beside an amount matches how that amount is formatted (a Dutch viewer sees
 * "US$ 1.234,56" in the cell and "US$" in the icon — never "$").
 * @param currency - ISO 4217 currency code
 * @param locale - Defaults to the viewer's UI-language locale
 */
export function getCurrencySymbol(currency: string = 'EUR', locale: string = getCurrencyLocale()): string {
  return getCurrencySymbolInLocale(currency, locale);
}

/**
 * Localized currency display name ("Euro", "Amerikaanse dollar") in the
 * viewer's locale, from `Intl.DisplayNames`. The only source of currency names
 * in the app — there is no name table.
 * @param currency - ISO 4217 currency code
 * @param locale - Defaults to the viewer's UI-language locale
 */
export function getCurrencyName(currency: string = 'EUR', locale: string = getCurrencyLocale()): string {
  return getCurrencyNameInLocale(currency, locale);
}

/**
 * Format an amount with organization's default currency using proper locale formatting
 * @param amount - The numeric amount to format
 * @param organization - Organization object with currency field
 * @returns Formatted currency string with locale-appropriate number formatting
 */
export function formatOrganizationCurrency(amount: number, organization?: { currency: string } | null): string {
  return formatOrganizationCurrencyForLanguage(amount, organization, i18n.language);
}

/**
 * Format an amount respecting account-level currency override
 * Account currency takes precedence over organization currency
 * @param amount - The numeric amount to format
 * @param account - Account object with optional currency field
 * @param organization - Organization object with currency field (fallback)
 * @returns Formatted currency string with locale-appropriate number formatting
 */
export function formatAccountCurrency(
  amount: number,
  account?: { currency?: string | null } | null,
  organization?: { currency: string } | null
): string {
  return formatAccountCurrencyForLanguage(amount, account, organization, i18n.language);
}

/**
 * Format an amount in the account-on-side currency, falling back to the
 * organization's base currency (or EUR if neither is set).
 *
 * @param amount Amount to format
 * @param entries Ledger transaction entries
 * @param side Which side to read for the account currency
 * @param organization Organization with base currency (fallback)
 */
export function formatEntryAccountCurrency(
  amount: number,
  entries: EntryWithAccount[] | undefined | null,
  side: 'debit' | 'credit',
  organization?: { currency?: string | null } | null,
): string {
  return formatEntryAccountCurrencyForLanguage(amount, entries, side, organization, i18n.language);
}

/**
 * Format multi-currency totals as a display string
 * @param totals - Object with currency codes and amounts
 * @param options - Formatting options
 * @returns Formatted string like "€12,500 + $5,000"
 *
 * @example
 * ```ts
 * const formatted = formatMultiCurrencyTotal({ EUR: 12500, USD: 5000 });
 * // Result: "€12,500 + $5,000"
 * ```
 */
export function formatMultiCurrencyTotal(
  totals: MultiCurrencyTotal,
  options?: MultiCurrencyTotalOptions
): string {
  return formatMultiCurrencyTotalForLanguage(totals, i18n.language, options);
}

// ============================================
// Currency Conversion Functions
// ============================================

export interface ConversionResult {
  convertedAmount: number;
  exchangeRate: number;
  rateDate: string;
}

/**
 * Convert an amount from one currency to another using stored exchange rates
 * @param amount - The amount to convert
 * @param fromCurrency - Source currency code
 * @param toCurrency - Target currency code
 * @param date - Optional date for historical rate lookup (defaults to today)
 * @returns Conversion result with amount, rate, and rate date
 */
export async function convertCurrency(
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  date?: Date
): Promise<ConversionResult> {
  // Same currency - no conversion needed
  if (fromCurrency.toUpperCase() === toCurrency.toUpperCase()) {
    return {
      convertedAmount: amount,
      exchangeRate: 1,
      rateDate: (date || new Date()).toISOString().split('T')[0]
    };
  }

  const { data, error } = await supabase.rpc('convert_currency', {
    p_amount: amount,
    p_from_currency: fromCurrency.toUpperCase(),
    p_to_currency: toCurrency.toUpperCase(),
    p_date: date?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0]
  });

  if (error) {
    throw new Error(`Currency conversion failed: ${error.message}`);
  }

  if (!data || data.length === 0) {
    throw new Error('No conversion result returned');
  }

  return {
    convertedAmount: data[0].converted_amount,
    exchangeRate: data[0].exchange_rate,
    rateDate: data[0].rate_date
  };
}

/**
 * Get the exchange rate between two currencies
 * @param fromCurrency - Source currency code
 * @param toCurrency - Target currency code
 * @param date - Optional date for historical rate lookup
 * @returns Exchange rate as a number
 */
export async function getExchangeRate(
  fromCurrency: string,
  toCurrency: string,
  date?: Date
): Promise<number> {
  if (fromCurrency.toUpperCase() === toCurrency.toUpperCase()) {
    return 1;
  }

  const { data, error } = await supabase.rpc('get_exchange_rate', {
    p_from_currency: fromCurrency.toUpperCase(),
    p_to_currency: toCurrency.toUpperCase(),
    p_date: date?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0]
  });

  if (error) {
    throw new Error(`Failed to get exchange rate: ${error.message}`);
  }

  return data;
}
